import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import React, { useState, useEffect } from "react";
import { Outlet, useOutletContext, useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "@/api/axios";

// ----------------------------------------------------------------------
// HELPER FUNCTIONS: Using localStorage for persistence
// ----------------------------------------------------------------------

const getUserKey = () => localStorage.getItem("userKey") || "default";

const getStoredState = (key, defaultValue) => {
    try {
        const scopedKey = key === 'selectedBins'
            ? `plaex_${getUserKey()}_selected_bins`
            : key;
        const storedValue = localStorage.getItem(scopedKey);
        return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    } catch (error) {
        console.error(`Error retrieving ${key} from localStorage:`, error);
        return defaultValue;
    }
};

const setStoredState = (key, value) => {
    try {
        const scopedKey = key === 'selectedBins'
            ? `plaex_${getUserKey()}_selected_bins`
            : key;
        if (key === 'selectedBins' && Array.isArray(value) && value.length === 0) return;
        localStorage.setItem(scopedKey, JSON.stringify(value));
    } catch (error) {
        console.error(`Error storing ${key} in localStorage:`, error);
    }
};

const SidebarLayout = () => {
    const [timeRangeDropdownOpen, setTimeRangeDropdownOpen] = useState(false);
    const [binDropdownOpen, setBinDropdownOpen] = useState(false);
    const [userRole, setUserRole] = useState("Basic");
    const navigate = useNavigate();
    const location = useLocation();

    const timeRangeOptions = ["Day", "Week", "Month", "3 Months", "6 Months", "Year"];

    const [selectedTimeRange, setSelectedTimeRange] = useState(
        () => getStoredState('selectedTimeRange', timeRangeOptions[2])
    );
    const [selectedBins, setSelectedBins] = useState(
        () => getStoredState('selectedBins', [])
    );
    const [allBins, setAllBins] = useState([]);

    const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(
        () => getStoredState('selectedYear', new Date().getFullYear().toString())
    );
    const yearOptions = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());

    const wasteTypeOptions = ["pmd", "organic", "paper", "residual"];

    const [wasteTypeDropdownOpen, setWasteTypeDropdownOpen] = useState(false);
    const [selectedWasteTypes, setSelectedWasteTypes] = useState(
        () => getStoredState('selectedWasteTypes', [])
    );

    const [selectedWaste, setSelectedWaste] = useState(
        () => getStoredState('selectedWaste', 'pmd')
    );

    const [customDateRange, setCustomDateRange] = useState(
        () => getStoredState('customDateRange', [{
            startDate: null,
            endDate: null,
            key: 'selection'
        }])
    );
    const [isCustomDateRange, setIsCustomDateRange] = useState(
        () => getStoredState('isCustomDateRange', false)
    );

    // ----------------------------------------------------------------------
    // EFFECTS: Sync state to localStorage
    // ----------------------------------------------------------------------

    useEffect(() => { setStoredState('selectedTimeRange', selectedTimeRange); }, [selectedTimeRange]);
    useEffect(() => { setStoredState('selectedBins', selectedBins); }, [selectedBins]);
    useEffect(() => { setStoredState('selectedYear', selectedYear); }, [selectedYear]);
    useEffect(() => { setStoredState('selectedWasteTypes', selectedWasteTypes); }, [selectedWasteTypes]);
    useEffect(() => { setStoredState('selectedWaste', selectedWaste); }, [selectedWaste]);
    useEffect(() => { setStoredState('customDateRange', customDateRange); }, [customDateRange]);
    useEffect(() => { setStoredState('isCustomDateRange', isCustomDateRange); }, [isCustomDateRange]);

    // ----------------------------------------------------------------------
    // DATA FETCHING
    // ----------------------------------------------------------------------

    useEffect(() => {
        const fetchUserRole = async () => {
            try {
                const response = await axiosInstance.get("usertier");
                console.log("role: ", response.data.tier);
                setUserRole(response.data.tier);

                if (response.data === "Basic") {
                    if (location.pathname !== '/operations') {
                        navigate('/operations');
                    }
                }
            } catch (error) {
                console.error("Error fetching user role:", error);
            }
        };
        fetchUserRole();
    }, [location.pathname, navigate]);

    useEffect(() => {
        const fetchBinIds = async () => {
            try {
                const response = await axiosInstance.get("bin_ids");
                const binIds = response.data || [];
                const validBinIds = binIds.filter(id => id && typeof id === 'string');
                setAllBins(validBinIds);

                const storedBins = getStoredState('selectedBins', []);

                if (storedBins.length > 0) {
                    const validStoredBins = storedBins.filter(bin => validBinIds.includes(bin));
                    if (validStoredBins.length > 0) {
                        setSelectedBins(validStoredBins);
                    } else {
                        setSelectedBins(validBinIds.slice(0, Math.min(3, validBinIds.length)));
                    }
                } else {
                    setSelectedBins(validBinIds.slice(0, Math.min(3, validBinIds.length)));
                }
            } catch (error) {
                console.error("Layout: Error fetching bin IDs:", error);
            }
        };
        fetchBinIds();
    }, []);

    // ----------------------------------------------------------------------
    // HANDLERS
    // ----------------------------------------------------------------------

    const selectTimeRange = (timeRange) => { setSelectedTimeRange(timeRange); return false; };

    const toggleBinSelection = (bin) => {
        setSelectedBins(prev => {
            return prev.includes(bin) ? prev.filter(item => item !== bin) : [...prev, bin];
        });
        return false;
    };

    const selectYear = (year) => { setSelectedYear(year); return false; };

    const toggleWasteTypeSelection = (wasteType) => {
        setSelectedWasteTypes(prev => {
            return prev.includes(wasteType) ? prev.filter(item => item !== wasteType) : [...prev, wasteType];
        });
        return false;
    };

    const selectWaste = (waste) => { setSelectedWaste(waste); };

    const handleCustomDateRangeChange = (dateRange, isCustom) => {
        setCustomDateRange(dateRange);
        setIsCustomDateRange(isCustom);
        if (!isCustom) setSelectedTimeRange(timeRangeOptions[2]);
    };

    const headerProps = {
        timeRangeDropdownOpen, setTimeRangeDropdownOpen,
        binDropdownOpen, setBinDropdownOpen,
        selectedTimeRange, selectTimeRange, timeRangeOptions,
        selectedBins, allBins, toggleBinSelection,
        yearDropdownOpen, setYearDropdownOpen,
        selectedYear, selectYear, yearOptions,
        wasteTypeDropdownOpen, setWasteTypeDropdownOpen,
        selectedWasteTypes, toggleWasteTypeSelection, wasteTypeOptions,
        selectedWaste, setSelectedWaste: selectWaste,
        customDateRange, onCustomDateRangeChange: handleCustomDateRangeChange,
        isCustomDateRange,
    };

    return (
        // defaultOpen={false} → sidebar starts collapsed/tucked in
        <SidebarProvider defaultOpen={false}>
            <AppSidebar variant="inset" userRole={userRole} />
            <SidebarInset>
                {location.pathname !== "/cost-savings" && location.pathname !== "/circularity" && (
                    <SiteHeader {...headerProps} />
                )}

                {!location.pathname.includes('/account') && location.pathname !== "/cost-savings" && location.pathname !== "/circularity" && (
                    <div className="h-4 md:h-6"></div>
                )}

                <div className="tab-content h-full">
                    <Outlet context={{
                        ...headerProps,
                        selectedTimeRange,
                        selectedBins,
                        selectedYear,
                        selectedWasteTypes,
                        selectedWaste,
                        userRole,
                        customDateRange,
                        isCustomDateRange,
                    }} />
                </div>
            </SidebarInset>
        </SidebarProvider>
    );
};

export default SidebarLayout;

export function useFilters() {
    try {
        return useOutletContext() || {
            selectedTimeRange: null, selectedBins: null,
            selectedYear: null, selectedWasteTypes: null,
            selectedWaste: null, userRole: null,
            customDateRange: null, isCustomDateRange: false,
        };
    } catch (error) {
        return {
            selectedTimeRange: null, selectedBins: null,
            selectedYear: null, selectedWasteTypes: null,
            selectedWaste: null, userRole: null,
            customDateRange: null, isCustomDateRange: false,
        };
    }
}